import React, { useEffect, useState } from "react";
// components
import { Button, Tabs, Tab, ListGroup, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ReactComponent as MagnifyingGlass } from "assets/svg/magnifying-glass.svg";
import { ReactComponent as OutlineEyeOpen } from "assets/svg/outline-eye-open.svg";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import querySchemas from "services/querySchemas";
import Checkbox from "components/checkbox/Checkbox";
import getClasses from "utils/getClasses";
import styles from "./ShareDocsModal.module.scss";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const ShareDocsModal = ({
  isInPortal,
  show,
  onHide,
  onConfirm,
  patientEntry,
  error,
  formPackages,
  coupleToShare,
  setCoupleToShare,
}: {
  isInPortal?: boolean;
  show: boolean;
  onHide: () => void;
  onConfirm: (arg) => void;
  patientEntry?: {
    isMinor: boolean;
    isCouple: boolean;
    couple_members;
  };
  error?: Error | string;
  formPackages?: boolean;
  coupleToShare?: {
    orchid_user_id: string;
  }[];
  setCoupleToShare?: (arg) => void;
}) => {
  const { isMinor, isCouple, couple_members } = patientEntry || {};
  const { practiceOwnerOrchidUserId } = useTeamContext();
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<Error | string>();
  const [tab, setTab] = useState("forms");

  const [formSearchTerm, setFormSearchTerm] = useState("");
  const [availableForms, setAvailableForms] = useState([] as any);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    async function initData() {
      setBusy(true);
      const availableForms = await querySchemas(
        "form",
        practiceOwnerOrchidUserId,
      );
      setAvailableForms(
        availableForms.filter((el) => el.form_type === "custom"),
      );
    }
    initData()
      .catch(setModalErr)
      .finally(() => setBusy(false));
    // eslint-disable-next-line
  }, [practiceOwnerOrchidUserId]);

  const onSearchChange = (e) => {
    setFormSearchTerm(e.currentTarget.value);
  };

  const onCheckBoxChange = (form) => (e) => {
    const newSelected = new Set(selected);
    if (e.target.checked) {
      newSelected.add(form.form_schema_id);
    } else {
      newSelected.delete(form.form_schema_id);
    }
    setSelected(newSelected);
  };

  const handleConfirmClick = () => {
    onConfirm(Array.from(selected));
    setSelected(new Set());
  };

  const handleHide = () => {
    onHide();
    setModalErr(undefined);
    setSelected(new Set());
    setFormSearchTerm("");
  };

  return (
    <ModalLayout
      size="lg"
      centered={true}
      showHeaderDivider={true}
      show={show}
      onHide={() => handleHide()}
      Header={"Share documents on Client Portal"}
      Footer={
        <>
          <Button disabled={busy} variant={"outline-primary"} onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={handleConfirmClick}
          >
            Share
          </Button>
        </>
      }
      headerClass={styles["share-docs__header"]}
      bodyClass={styles["share-docs__body"]}
      footerClass={styles["share-docs__footer"]}
      dialogClassName={styles["share-docs__dialog"]}
      className={styles["share-docs__modal"]}
    >
      <div className={styles["share-docs__sticky"]}>
        {error ? (
          <Alert variant="danger">{error.toString()}</Alert>
        ) : modalErr ? (
          <Alert variant="danger">{modalErr.toString()}</Alert>
        ) : null}
        {!formPackages && (
          <div className={styles["share-docs__sticky-upper"]}>
            <div className={"bd-16"}>Who do you want to send to?</div>
            <div className={"rg-16 mb-4"}>
              All items that are shared to one party will not be shared to
              another, unless permission is granted
            </div>
            {!isCouple && !isMinor ? (
              <Checkbox
                variant={"rectangle"}
                checked
                label={"Patient"}
                readOnly
              />
            ) : isMinor && !isCouple ? (
              <Checkbox
                variant={"rectangle"}
                checked
                label={"Contact 1"}
                readOnly
              />
            ) : isCouple && !isMinor ? (
              <div className={"d-flex"}>
                <div className={styles["share-docs__control"]}>
                  <CustomSelectionControl
                    type={"checkbox"}
                    checked={coupleToShare?.some(
                      (el) =>
                        el.orchid_user_id === couple_members[0]?.orchid_user_id,
                    )}
                    onChange={() => {
                      setCoupleToShare &&
                        setCoupleToShare((s) =>
                          s?.some(
                            (el) =>
                              el.orchid_user_id ===
                              couple_members[0]?.orchid_user_id,
                          )
                            ? s.filter(
                                (el) =>
                                  el.orchid_user_id !==
                                  couple_members[0]?.orchid_user_id,
                              )
                            : [
                                ...s,
                                {
                                  orchid_user_id:
                                    couple_members[0]?.orchid_user_id,
                                },
                              ],
                        );
                    }}
                  />
                  Patient 1
                  {couple_members[0]?.first_name
                    ? `: ${couple_members[0].first_name}`
                    : ""}{" "}
                  {couple_members[0]?.last_name || ""}
                </div>
                <div className={styles["share-docs__control"]}>
                  <CustomSelectionControl
                    type={"checkbox"}
                    checked={coupleToShare?.some(
                      (el) =>
                        el.orchid_user_id === couple_members[1]?.orchid_user_id,
                    )}
                    onChange={() => {
                      setCoupleToShare &&
                        setCoupleToShare((s) =>
                          s?.some(
                            (el) =>
                              el.orchid_user_id ===
                              couple_members[1]?.orchid_user_id,
                          )
                            ? s.filter(
                                (el) =>
                                  el.orchid_user_id !==
                                  couple_members[1]?.orchid_user_id,
                              )
                            : [
                                ...s,
                                {
                                  orchid_user_id:
                                    couple_members[1]?.orchid_user_id,
                                },
                              ],
                        );
                    }}
                  />
                  Patient 2
                  {couple_members[1]?.first_name
                    ? `: ${couple_members[1].first_name}`
                    : ""}{" "}
                  {couple_members[1]?.last_name || ""}
                </div>
              </div>
            ) : null}
          </div>
        )}

        <Tabs
          activeKey={tab}
          id="note-form-controller"
          onSelect={(k) => {
            setTab((s) => k || s);
          }}
          className={styles["share-docs__tabs"]}
        >
          {[{ value: "forms", label: "Forms" }].map((el, i) => (
            <Tab
              key={i}
              eventKey={el.value}
              title={el.label}
              tabClassName={styles["share-docs__tab"]}
            ></Tab>
          ))}
        </Tabs>
        <div className={styles["share-docs__sticky-search"]}>
          <div className="mb-2 position-relative">
            <input
              className="form-control"
              type="text"
              style={{ display: "inline" }}
              value={formSearchTerm}
              onChange={onSearchChange}
              placeholder="Search my forms"
            />
            <MagnifyingGlass
              style={{
                position: "absolute",
                right: "0.8rem",
                top: "0.6rem",
                fontSize: "1.2rem",
              }}
            />
          </div>
          <div className={"rg-12 gray01"}>
            Sharing documents with patients will allow the patients to sign up
            for Orchid Client Portal account.
          </div>
        </div>
      </div>
      <ListGroup
        className={getClasses(
          !isInPortal
            ? styles["share-docs__list-group--form-packages"]
            : styles["share-docs__list-group"],
        )}
      >
        {availableForms
          .filter(
            (form) =>
              formSearchTerm.length === 0 ||
              form.title.toUpperCase().includes(formSearchTerm.toUpperCase()),
          )
          .map((form) => (
            <ListGroup.Item
              className={styles["share-docs__list-item"]}
              key={form.form_schema_id}
            >
              <Checkbox
                variant={"rectangle"}
                onChange={onCheckBoxChange(form)}
                checked={selected.has(form.form_schema_id)}
              />
              {form.title}
              <div className="ml-auto">
                <Button
                  as={Link as any}
                  to={{
                    pathname: `/pro-dashboard/notes-and-forms/view-form-template/${form.form_schema_id}`,
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="link"
                  size="sm"
                  className="darker"
                >
                  <OutlineEyeOpen />
                </Button>
              </div>
            </ListGroup.Item>
          ))}
      </ListGroup>
    </ModalLayout>
  );
};

export default ShareDocsModal;
